import { useEffect } from 'react';
import { useTrialLockout } from '@discover-frontend/components';

import { useGetTrialStatus } from '../../api/hooks/use-get-trial-status';
import { isTrialActive } from '../../api/request';

type TrialBootstrapGateParams = {
  isTrial: boolean;
  sessionReady: boolean;
};

export type TrialBootstrapGate = {
  /** Authenticated bootstrap (me / products / company) may run. */
  canBootstrapSession: boolean;
  /** Hold the app shell: trial status is still resolving, or the trial is over. */
  isTrialBootstrapBlocked: boolean;
};

/**
 * Resolves trial status before the authenticated bootstrap for trial users only;
 * non-trial users are unaffected. A failed status call fails open, so an outage
 * on that endpoint cannot lock every trial user out of the app.
 */
export const useTrialBootstrapGate = ({
  isTrial,
  sessionReady,
}: TrialBootstrapGateParams): TrialBootstrapGate => {
  const { markSessionQuotaExhausted } = useTrialLockout();
  const isGateEnabled = isTrial && sessionReady;
  const { data, isLoading, error } = useGetTrialStatus(isGateEnabled);

  const isResolvingTrialStatus = isGateEnabled && isLoading;
  const isTrialInactive =
    isGateEnabled && !error && Boolean(data) && !isTrialActive(data);

  useEffect(() => {
    if (isTrialInactive) markSessionQuotaExhausted();
  }, [isTrialInactive, markSessionQuotaExhausted]);

  return {
    canBootstrapSession:
      sessionReady && !isResolvingTrialStatus && !isTrialInactive,
    isTrialBootstrapBlocked: isResolvingTrialStatus || isTrialInactive,
  };
};
